import React, { useEffect, useRef } from 'react';

import { useStorage } from '~Components/Storage';
import { useDispatch, useLogger, useSelector } from '~Hooks';
import * as actions from '~actions';

/**
 * Feeds persisted orders into the existing, unchanged order sync flow.
 *
 * 1. Every record in `storage.pendingOrderSyncs` that has not been replayed in this
 *    app session is dispatched as SYNC_POS_ORDER. That is the same action the payment
 *    flows dispatch themselves, so from here on nothing differs from today.
 * 2. A record is removed from storage only after it has been observed in
 *    `POSOrdersForSync` and then left it, which happens on POS_ORDER_SYNCED, i.e. after
 *    createPosOrder succeeded.
 *
 * Renders nothing. With `pendingOrderSyncs` empty it does nothing.
 */
const PersistedOrderSyncReplayer: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const logger = useLogger('persisted-order-sync');
  const [storage, updateStorage] = useStorage();
  const ordersForSync = useSelector((state) => state.POSOrdersForSync);

  const storageRef = useRef(storage);
  useEffect(() => {
    storageRef.current = storage;
  }, [storage]);

  const replayed = useRef(new Set<string>());
  const seenInRedux = useRef(new Set<string>());

  useEffect(() => {
    storage.pendingOrderSyncs.forEach((persisted) => {
      const orderId = persisted.order.id;

      if (replayed.current.has(orderId)) {
        return;
      }
      replayed.current.add(orderId);

      if (ordersForSync.some((o) => o.order.id === orderId)) {
        seenInRedux.current.add(orderId);
        return;
      }

      logger.info(`Replaying persisted order ${orderId} into order sync`, {
        orderId,
        amount: persisted.amount,
        method: persisted.payment.method,
        transactionId: persisted.payment.meta?.transactionId,
      });

      dispatch({
        type: actions.SYNC_POS_ORDER,
        ...persisted,
      });
    });
  }, [storage.pendingOrderSyncs]);

  useEffect(() => {
    ordersForSync.forEach((o) => {
      if (replayed.current.has(o.order.id)) {
        seenInRedux.current.add(o.order.id);
      }
    });

    const synced = storageRef.current.pendingOrderSyncs.filter(
      (persisted) =>
        seenInRedux.current.has(persisted.order.id) &&
        !ordersForSync.some((o) => o.order.id === persisted.order.id),
    );

    if (synced.length === 0) {
      return;
    }

    const syncedIds = synced.map((persisted) => persisted.order.id);

    updateStorage({
      pendingOrderSyncs: storageRef.current.pendingOrderSyncs.filter(
        (persisted) => !syncedIds.includes(persisted.order.id),
      ),
    });

    syncedIds.forEach((orderId) => {
      seenInRedux.current.delete(orderId);
      logger.info(`Persisted order ${orderId} synced, removed from storage`, { orderId });
    });
  }, [ordersForSync]);

  return null;
};

export default PersistedOrderSyncReplayer;
